import { api } from 'app/app.api';

import {
  CreateSecretApiArg,
  CreateSecretApiResponse,
  CreateSecretRequest,
  DecryptPayloadRequest,
  DecryptTokenApiArg,
  DecryptTokenApiResponse,
  DeleteSecretApiArg,
  DeleteSecretApiResponse,
  GenerateSecretApiArg,
  GenerateSecretApiResponse,
  GenerateSecretRequest,
  PatchSecretRequest,
  ReadGlobalFallbackSettingsApiArg,
  ReadGlobalFallbackSettingsResponse,
  ReadSecretsApiArg,
  ReadSecretsApiResponse,
  SecretInformation,
  UpdateSecretApiArg,
  UpdateSecretApiResponse,
  VerifyElevioAccountApiArg,
  VerifyElevioAccountResponse,
} from './types/messengersApi.types';

const baseUrl = '/v2/messengers';
const mkSecretsUrl = (messengerId: string) =>
  `${baseUrl}/${messengerId}/secrets`;

const mkSecretUrl = (messengerId: string, secretId: string) =>
  `${mkSecretsUrl(messengerId)}/${secretId}`;

const apiWithTags = api.enhanceEndpoints({ addTagTypes: ['MessengerSecret'] });
export const messengersApi = apiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    createSecret: builder.mutation<CreateSecretApiResponse, CreateSecretApiArg>(
      {
        query: ({ messengerId, secret, description }) => {
          const data: CreateSecretRequest = {
            encryption_type: 'jwe_symmetric',
            secret,
            description,
          };
          return {
            path: mkSecretsUrl(messengerId),
            method: 'POST',
            body: data,
          };
        },
        transformResponse: (response) => {
          const data = response as SecretInformation;
          return {
            id: data.id,
            createdAt: data.created_at,
            description: data.description,
          };
        },
        invalidatesTags: (_result, error, arg) =>
          error ? [] : [{ type: 'MessengerSecret', id: arg.messengerId }],
      },
    ),
    readSecrets: builder.query<ReadSecretsApiResponse, ReadSecretsApiArg>({
      query: ({ messengerId }) => ({ path: mkSecretsUrl(messengerId) }),
      transformResponse: (response) => {
        return (response as SecretInformation[]).map((x) => ({
          id: x.id,
          createdAt: x.created_at,
          description: x.description,
        }));
      },
      providesTags: (_result, _error, arg) => [
        { type: 'MessengerSecret', id: arg.messengerId },
      ],
    }),
    updateSecret: builder.mutation<UpdateSecretApiResponse, UpdateSecretApiArg>(
      {
        query: ({ messengerId, secretId, description }) => {
          const data: PatchSecretRequest = {
            description,
          };
          return {
            path: mkSecretUrl(messengerId, secretId),
            method: 'PUT',
            body: data,
          };
        },
        transformResponse: (response) => {
          const data = response as SecretInformation;
          return {
            id: data.id,
            createdAt: data.created_at,
            description: data.description,
          };
        },
        invalidatesTags: (_result, error, arg) =>
          error ? [] : [{ type: 'MessengerSecret', id: arg.messengerId }],
      },
    ),
    deleteSecret: builder.mutation<DeleteSecretApiResponse, DeleteSecretApiArg>(
      {
        query: ({ messengerId, secretId }) => {
          return {
            path: mkSecretUrl(messengerId, secretId),
            method: 'DELETE',
          };
        },
        invalidatesTags: (_result, error, arg) =>
          error ? [] : [{ type: 'MessengerSecret', id: arg.messengerId }],
      },
    ),
    generateSecret: builder.mutation<
      GenerateSecretApiResponse,
      GenerateSecretApiArg
    >({
      query: () => {
        const data: GenerateSecretRequest = {
          algorithm: 'A256CBC-HS512',
        };
        return {
          path: `${baseUrl}/generate-secret`,
          method: 'POST',
          body: data,
        };
      },
    }),
    decryptToken: builder.mutation<DecryptTokenApiResponse, DecryptTokenApiArg>(
      {
        query: ({ messengerId, payload }) => {
          const data: DecryptPayloadRequest = {
            payload,
          };
          return {
            path: `${baseUrl}/${messengerId}/decrypt`,
            method: 'POST',
            body: data,
          };
        },
      },
    ),

    verifyElevioAccount: builder.query<
      VerifyElevioAccountResponse,
      VerifyElevioAccountApiArg
    >({
      query: ({ accountId }) => ({
        path: `${baseUrl}/elevio-accounts/${accountId}`,
      }),
    }),

    readGlobalFallbackSettings: builder.query<
      ReadGlobalFallbackSettingsResponse,
      ReadGlobalFallbackSettingsApiArg
    >({
      query: () => ({
        path: `${baseUrl}/default-settings`,
      }),
    }),
  }),
});
